import { Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { fromEvent, map, Observable, of, Subscription, switchMap } from 'rxjs';
import { BaseComponent } from '../components/base.component';
import { MessageService } from '../services/message.service';
import { TranslateService } from '@ngx-translate/core';

export default class FormHelper {
  static dirtyCheckBeforeUnload(window: Window | null, observable: Observable<boolean>, component: BaseComponent): Subscription {
    if (!window) {
      return of(false).subscribe();
    }

    return fromEvent(window, 'beforeunload')
      .unsubscribeOnDestroy(component)
      .subscribe(event => {
        const subscription = observable.subscribe(value => {
          if (value) {
            event.returnValue = false;
          }
        });

        subscription.unsubscribe();
      });
  }

  static dirtyCheckMultipleFormGroups(formGroups: () => AbstractControl[]): boolean {
    return formGroups().some(control => control?.dirty);
  }

  // Focusses the first invalid control
  static focusFirstInvalidFormControl(document: Document): Element | any {
    // Add more selectors here
    const invalidControls = document.querySelectorAll('mat-radio-group.ng-invalid, mat-select.ng-invalid, input.ng-invalid, textarea.ng-invalid, mat-chip-listbox.ng-invalid');

    if (invalidControls.length) {
      const invalidControl = invalidControls[0];
      invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (invalidControl as any).focus?.();

      return invalidControl;
    }
  }

  // Currently using the dashboard as navigation url.
  // This makes the redirect less visible inside the portal.
  static redirectTo(router: Router, uri: string): Promise<boolean> {
    return router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
      router.navigate([uri]));
  }

  static redirectToSelf(router: Router): any {
    return this.redirectTo(router, router.url);
  }

  static getFileSize(bytes: number): string {
    let size: number;
    let unit: string;

    if (bytes < 1024) {
      size = bytes;
      unit = "B";
    }
    else if (bytes < 1024 * 1024) {
      size = bytes / 1024;
      unit = "kB";
    }
    else {
      size = bytes / (1024 * 1024);
      unit = "MB";
    }

    return `${size.toFixed(2)} ${unit}`;
  }

  static createFileFormGroup(): FormGroup<{
    id: FormControl<string | null>,
    data: FormControl<string | null>,
    name: FormControl<string | null>,
    type: FormControl<string | null>
  }> {
    return new FormGroup({
      id: new FormControl<string | null>(null),
      data: new FormControl<string>(''),
      name: new FormControl<string>(''),
      type: new FormControl<string>('')
    });
  }

  static getControlCharacterCount(control: AbstractControl): number {
    const length = control.value?.toString().length;

    if (!length) {
      return 0;
    }

    return length;
  }

  static compareIds = (value1: any, value2: any) => {
    return value1?.id === value2?.id;
  }

  static toggleValidatorForControls(formControls: FormControl[], hasValidator: boolean, validator: ValidatorFn = Validators.required): void {
    formControls.forEach(control => {
      if (hasValidator) {
        control.addValidators(validator);
      }
      else {
        control.removeValidators(validator);
      }

      control.updateValueAndValidity();
    });
  }

  static setDefaultValueForControls(formControls: FormControl[], set: boolean, defaultValue: any): void {
    formControls.forEach(control => {
      if (set) {
        control.setValue(defaultValue);
      }
    });
  }
}
